import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useSharedMailboxesQuery } from '../../../../queries/User/useSharedMailboxes';
import { Label } from '../../../components-v2/Label';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../components-v2/Select';
import { styleSheet } from './styles';

interface IProps {
	value?: string;
	onChange: (emailAddress?: string) => void;
	disabled?: boolean;
}

const PRIMARY_VALUE = 'primary';

const getPrimaryAddress = (user?: Api.IUser): string | undefined =>
	user?.connectedEmailAddress || user?.primaryEmail?.value || user?.email;

/**
 * Mailbox-only send-from picker for one-to-one email, where there is no on-behalf mode to choose. Sends go from
 * the user's own mailbox unless they pick a shared mailbox here.
 */
const _SendFromMailboxSelect: React.FC<IProps & IImpersonationContextComponentProps> = ({
	value,
	onChange,
	disabled,
	impersonationContext,
}) => {
	const userSession = useUserSession();
	const labelId = React.useId();
	const { data } = useSharedMailboxesQuery({
		impersonationContext,
		meta: {
			errorLogging: { category: 'SendFromMailboxSelect', action: 'SendFromMailboxSelectSharedMailboxQuery-Error' },
		},
	});

	const mailboxes = data?.available === false ? [] : (data?.mailboxes ?? []);
	const selectable = mailboxes.filter(x => !data?.reconnectRequired && (!data?.verificationRequired || x.verified));

	// A chosen mailbox can stop being sendable (removed, unverified, reconnect required) while the composer
	// is open; fall back to the primary address rather than leaving an address the API will reject.
	const selectableKey = selectable.map(x => x.emailAddress).join(',');
	React.useEffect(() => {
		if (data && value && !selectable.some(x => x.emailAddress === value)) {
			onChange(undefined);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value, selectableKey, data]);

	if (mailboxes.length === 0) {
		return null;
	}

	const primaryAddress = getPrimaryAddress(userSession?.user);

	return (
		<div className={css(styleSheet.container)}>
			<Label id={labelId} styles={[styleSheet.label]}>
				Send from
			</Label>
			<Select
				value={value || PRIMARY_VALUE}
				disabled={disabled}
				onValueChange={(next: string) => onChange(next === PRIMARY_VALUE ? undefined : next)}
			>
				<SelectTrigger aria-labelledby={labelId} styles={[styleSheet.trigger]}>
					<span>{value ? (mailboxes.find(x => x.emailAddress === value)?.name ?? value) : primaryAddress}</span>
				</SelectTrigger>
				<SelectContent>
					<SelectItem value={PRIMARY_VALUE}>{primaryAddress}</SelectItem>
					{selectable.map(mailbox => (
						<SelectItem key={mailbox.id} value={mailbox.emailAddress}>
							{mailbox.name || mailbox.emailAddress}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</div>
	);
};

export const SendFromMailboxSelect = inject(ImpersonationContextKey)(observer(_SendFromMailboxSelect));
